/**
 * Helpers to enable Immutable compatibility *without* bringing in
 * the 'immutable' package as a dependency.
 */

/**
 * Check if an object is immutable by checking if it has a key specific
 * to the immutable library.
 *
 * @param  {any} object
 * @return {bool}
 */
function isImmutable(object) {
  return !!(object && typeof object.hasOwnProperty === 'function' && (Object.hasOwnProperty.call(object, '__ownerID') || // Immutable.Map
  object._map && Object.hasOwnProperty.call(object._map, '__ownerID'))); // Immutable.Record
}
/**
 * Denormalize an immutable entity.
 *
 * @param  {Schema} schema
 * @param  {Immutable.Map|Immutable.Record} input
 * @param  {function} unvisit
 * @param  {function} getDenormalizedEntity
 * @return {Immutable.Map|Immutable.Record}
 */

function denormalizeImmutable(schema, input, unvisit) {
  let found = true;
  return [Object.keys(schema).reduce((object, key) => {
    // Immutable maps cast keys to strings on write so we need to ensure
    // we're accessing them using string keys.
    const stringKey = `${key}`;
    const [item, foundItem] = unvisit(object.get(stringKey), schema[stringKey]);

    if (!foundItem) {
      found = false;
    }

    if (object.has(stringKey)) {
      return object.set(stringKey, item);
    } else {
      return object;
    }
  }, input), found];
}

class PolymorphicSchema {
  constructor(definition, schemaAttribute) {
    if (schemaAttribute) {
      this._schemaAttribute = typeof schemaAttribute === 'string' ? input => input[schemaAttribute] : schemaAttribute;
    }

    this.define(definition);
  }

  get isSingleSchema() {
    return !this._schemaAttribute;
  }

  define(definition) {
    this.schema = definition;
  }

  getSchemaAttribute(input, parent, key) {
    return !this.isSingleSchema && this._schemaAttribute(input, parent, key);
  }

  inferSchema(input, parent, key) {
    if (this.isSingleSchema) {
      return this.schema;
    }

    const attr = this.getSchemaAttribute(input, parent, key);
    return this.schema[attr];
  }

  normalizeValue(value, parent, key, visit, addEntity, visitedEntities) {
    const schema = this.inferSchema(value, parent, key);

    if (!schema) {
      return value;
    }

    const normalizedValue = visit(value, parent, key, schema, addEntity, visitedEntities);
    return this.isSingleSchema || normalizedValue === undefined || normalizedValue === null ? normalizedValue : {
      id: normalizedValue,
      schema: this.getSchemaAttribute(value, parent, key)
    };
  }

  denormalizeValue(value, unvisit) {
    const schemaKey = isImmutable(value) ? value.get('schema') : value.schema;

    if (!this.isSingleSchema && !schemaKey) {
      return [value, true];
    }

    const id = this.isSingleSchema ? undefined : isImmutable(value) ? value.get('id') : value.id;
    const schema = this.isSingleSchema ? this.schema : this.schema[schemaKey];
    return unvisit(id || value, schema);
  }

}

const validateSchema = definition => {
  const isArray = Array.isArray(definition);

  if (isArray && definition.length > 1) {
    throw new Error(`Expected schema definition to be a single schema, but found ${definition.length}.`);
  }

  return definition[0];
};

const getValues = input => Array.isArray(input) ? input : Object.keys(input).map(key => input[key]);

const normalize = (schema, input, parent, key, visit, addEntity, visitedEntities) => {
  schema = validateSchema(schema);
  const values = getValues(input); // Special case: Arrays pass *their* parent on to their children, since there
  // is not any special information that can be gathered from themselves directly

  return values.map((value, index) => visit(value, parent, key, schema, addEntity, visitedEntities));
};
const denormalize = (schema, input, unvisit) => {
  schema = validateSchema(schema);
  let found = true;

  if (input === undefined && schema) {
    [, found] = unvisit(undefined, schema);
  }

  return [input && input.map ? input.map(entityOrId => unvisit(entityOrId, schema)).filter(([, foundItem]) => foundItem).map(([value]) => value) : input, found];
};
class ArraySchema extends PolymorphicSchema {
  normalize(input, parent, key, visit, addEntity, visitedEntities) {
    const values = getValues(input);
    return values.map((value, index) => this.normalizeValue(value, parent, key, visit, addEntity, visitedEntities)).filter(value => value !== undefined && value !== null);
  }

  denormalize(input, unvisit) {
    let found = true;

    if (input === undefined && this.schema) {
      [, found] = unvisit(undefined, this.schema);
    }

    return [input && input.map ? input.map(entityOrId => this.denormalizeValue(entityOrId, unvisit)).filter(([, foundItem]) => foundItem).map(([value]) => value) : input, found];
  }

}

const normalize$1 = (schema, input, parent, key, visit, addEntity, visitedEntities) => {
  const object = Object.assign({}, input);
  Object.keys(schema).forEach(key => {
    const localSchema = schema[key];
    const value = visit(input[key], input, key, localSchema, addEntity, visitedEntities);

    if (value === undefined || value === null) {
      delete object[key];
    } else {
      object[key] = value;
    }
  });
  return object;
};
const denormalize$1 = (schema, input, unvisit) => {
  if (isImmutable(input)) {
    return denormalizeImmutable(schema, input, unvisit);
  }

  const object = Object.assign({}, input);
  let found = true;
  Object.keys(schema).forEach(key => {
    const [item, foundItem] = unvisit(object[key], schema[key]);

    if (object[key] !== undefined) {
      object[key] = item;
    }

    if (!foundItem) {
      found = false;
    }
  });
  return [object, found];
};
class ObjectSchema {
  constructor(definition) {
    this.define(definition);
  }

  define(definition) {
    this.schema = Object.keys(definition).reduce((entitySchema, key) => {
      const schema = definition[key];
      return Object.assign(Object.assign({}, entitySchema), {}, {
        [key]: schema
      });
    }, this.schema || {});
  }

  normalize(...args) {
    return normalize$1(this.schema, ...args);
  }

  denormalize(...args) {
    return denormalize$1(this.schema, ...args);
  }

}

const DefinedMembersKey = Symbol('Defined Members');
const UniqueIdentifierKey = Symbol('unq');

/** Immutable record that keeps track of which members are defined vs defaults. */
class SimpleRecord {
  toString() {
    // we don't make _unq a member so it doesn't play a role in type compatibility
    return this[UniqueIdentifierKey];
  }
  /** Defines nested entities */


  /** Factory method to convert from Plain JS Objects.
   *
   * @param [props] Plain Object of properties to assign.
   * @param [parent] When normalizing, the object which included the record
   * @param [key] When normalizing, the key where this record was found
   */
  static fromJS( // TODO: this should only accept members that are not functions
  props = {}, parent, key) {
    // we type guarded abstract case above, so ok to force typescript to allow constructor call
    const instance = new this(props);
    Object.assign(instance, props);
    Object.defineProperty(instance, DefinedMembersKey, {
      value: Object.keys(props),
      writable: false
    }); // a 'unique' identifier to make referential equality comparisons easy

    Object.defineProperty(instance, UniqueIdentifierKey, {
      value: `${Math.random()}`,
      writable: false
    });
    return instance;
  }
  /** Creates new instance copying over defined values of arguments */


  static merge(existing, incoming) {
    const props = Object.assign(this.toObjectDefined(existing), this.toObjectDefined(incoming));
    return this.fromJS(props);
  }
  /** Whether key is non-default */


  static hasDefined(instance, key) {
    return instance[DefinedMembersKey].includes(key);
  }
  /** Returns simple object with all the non-default members */


  static toObjectDefined(instance) {
    const defined = {};

    for (const member of instance[DefinedMembersKey]) {
      defined[member] = instance[member];
    }

    return defined;
  }
  /** Returns array of all keys that have values defined in instance */


  static keysDefined(instance) {
    return instance[DefinedMembersKey];
  }

  static normalize(...args) {
    return normalize$1(this.schema, ...args);
  }

  static denormalize(...args) {
    const [res, found] = denormalize$1(this.schema, ...args); // useDenormalized will memo based on entities, so creating a new object each time is fine

    return [this.fromJS(res), found];
  }
  /* istanbul ignore next */


  static asSchema() {
    /* istanbul ignore next */
    if (process.env.NODE_ENV === 'development') {
      console.error('asSchema() is deprecated - use Entity directly instead.');
    }
    /* istanbul ignore next */


    return this;
  }

}
SimpleRecord.schema = {};

/** Represents data that should be deduped by specifying a primary key. */
class Entity extends SimpleRecord {
  /**
   * A unique identifier for each Entity
   *
   * @param [parent] When normalizing, the object which included the entity
   * @param [key] When normalizing, the key where this entity was found
   */

  /** Returns the globally unique identifier for the static Entity */
  static get key() {
    /* istanbul ignore next */
    if (process.env.NODE_ENV !== 'production' && this.name === '') throw new Error('Entity classes without a name must define `static get key()`');
    return this.name;
  }
  /** Defines indexes to enable lookup by */


  /**
   * A unique identifier for each Entity
   *
   * @param [value] POJO of the entity or subset used
   * @param [parent] When normalizing, the object which included the entity
   * @param [key] When normalizing, the key where this entity was found
   */
  static pk(value, parent, key) {
    return this.prototype.pk.call(value, parent, key) || key;
  }

  static normalize(input, parent, key, visit, addEntity, visitedEntities) {
    // TODO: what's store needs to be a differing type from fromJS
    const processedEntity = this.fromJS(input, parent, key);
    /* istanbul ignore else */

    if (process.env.NODE_ENV !== 'production') {
      const instanceSample = new this();
      const keysOfRecord = new Set(Object.keys(instanceSample));
      const keysOfProps = this.keysDefined(processedEntity);
      const [found, missing, unexpected] = [[], [], []];

      for (const keyOfProps of keysOfProps) {
        if (keysOfRecord.has(keyOfProps)) {
          found.push(keyOfProps);
        } else {
          unexpected.push(keyOfProps);
        }
      }

      for (const keyOfRecord of keysOfRecord) {
        if (!found.includes(keyOfRecord)) {
          missing.push(keyOfRecord);
        }
      } // if we find nothing, or we find too many unexpected members


      if ((Math.max(keysOfProps.length / 2, 1) <= unexpected.length && keysOfRecord.size > Math.max(unexpected.length, 2) || found.length < Math.min(1, keysOfRecord.size / 2)) && keysOfRecord.size) {
        const error = new Error(`Attempted to initialize ${this.name} with substantially different than expected keys

  This is likely due to a malformed response.
  Try inspecting the network response or fetch() return value.

  Expected keys:
    Found: ${found}
    Missing: ${missing}
  Unexpected keys: ${unexpected}
  Value: ${JSON.stringify(this.toObjectDefined(processedEntity), null, 2)}`);
        error.status = 400;
        throw error;
      }
    }

    const id = processedEntity.pk(parent, key);

    if (id === undefined || id === '') {
      if (process.env.NODE_ENV !== 'production') {
        const error = new Error(`Missing usable resource key when normalizing response.

  This is likely due to a malformed response.
  Try inspecting the network response or fetch() return value.

  Entity: ${this.name}
  Value: ${input && JSON.stringify(input, null, 2)}
  `);
        error.status = 400;
        throw error;
      } else {
        // these make the keys get deleted
        return undefined;
      }
    }

    const entityType = this.key;

    if (!(entityType in visitedEntities)) {
      visitedEntities[entityType] = {};
    }

    if (!(id in visitedEntities[entityType])) {
      visitedEntities[entityType][id] = [];
    }

    if (visitedEntities[entityType][id].some(entity => entity === input)) {
      return id;
    }

    visitedEntities[entityType][id].push(input);
    Object.keys(this.schema).forEach(key => {
      if (Object.hasOwnProperty.call(processedEntity, key) && typeof processedEntity[key] === 'object') {
        const schema = this.schema[key];
        processedEntity[key] = visit(processedEntity[key], processedEntity, key, schema, addEntity, visitedEntities);
      }
    });
    addEntity(this, processedEntity, processedEntity, parent, key);
    return id;
  }

  static denormalize(entity, unvisit) {
    if (isImmutable(entity)) {
      const [denormEntity, found] = denormalizeImmutable(this.schema, entity, unvisit);
      return [this.fromJS(denormEntity.toObject()), found];
    }

    let found = true;
    const denormEntity = entity;
    Object.keys(this.schema).forEach(key => {
      const schema = this.schema[key];
      const [value, foundItem] = unvisit(entity[key], schema);

      if (!foundItem) {
        found = false;
      }

      if (this.hasDefined(entity, key) && denormEntity[key] !== value) {
        denormEntity[key] = value;
      }
    });
    return [denormEntity, found];
  }

}
/* istanbul ignore next */

if (process.env.NODE_ENV !== 'production') {
  // for those not using TypeScript this is a good catch to ensure they are defining
  // the abstract members
  Entity.fromJS = function fromJS(props) {
    if (this.prototype.pk === undefined) throw new Error('cannot construct on abstract types');
    return SimpleRecord.fromJS.call(this, props);
  };
}

function isEntity(schema) {
  return schema !== null && schema.pk !== undefined;
}

class FlatEntity extends Entity {
  static denormalize(entity, unvisit) {
    return [entity, true];
  }

}

const unvisitEntity = (id, schema, unvisit, getEntity, cache) => {
  const entity = getEntity(id, schema);

  if (typeof entity !== 'object' || entity === null) {
    return [entity, false];
  }

  if (!cache[schema.key]) {
    cache[schema.key] = {};
  }

  let found = true;

  if (!cache[schema.key][id]) {
    // Ensure we don't mutate it non-immutable objects
    const entityCopy = isImmutable(entity) || entity instanceof FlatEntity ? entity : schema.fromJS(entity); // Need to set this first so that if it is referenced further within the
    // denormalization the reference will already exist.

    cache[schema.key][id] = entityCopy;
    [cache[schema.key][id], found] = schema.denormalize(entityCopy, unvisit);
  }

  return [cache[schema.key][id], found];
};

const getUnvisit = entities => {
  const cache = {};
  const getEntity = getEntities(entities);
  return [function unvisit(input, schema) {
    if (!schema) return [input, true];

    if (typeof schema === 'object' && (!schema.denormalize || typeof schema.denormalize !== 'function')) {
      const method = Array.isArray(schema) ? denormalize : denormalize$1;
      return method(schema, input, unvisit);
    } // null is considered intentional, thus always 'found' as true


    if (input === null) {
      return [input, true];
    }

    if (isEntity(schema)) {
      // unvisitEntity just can't handle undefined
      if (input === undefined) {
        return [input, false];
      }

      return unvisitEntity(input, schema, unvisit, getEntity, cache);
    }

    if (typeof schema.denormalize === 'function') {
      return schema.denormalize(input, unvisit);
    }

    return [input, true];
  }, cache];
};

const getEntities = entities => {
  const isImmutable$1 = isImmutable(entities);
  return (entityOrId, schema) => {
    const schemaKey = schema.key;

    if (typeof entityOrId === 'object') {
      return entityOrId;
    }

    if (isImmutable$1) {
      return entities.getIn([schemaKey, entityOrId]);
    }

    return entities[schemaKey] && entities[schemaKey][entityOrId];
  };
};

const denormalize$2 = (input, schema, entities) => {
  /* istanbul ignore next */
  if (process.env.NODE_ENV !== 'production' && schema === undefined) throw new Error('schema needed');

  if (typeof input !== 'undefined') {
    const [unvisit, cache] = getUnvisit(entities);
    return [...unvisit(input, schema), cache];
  }

  return [undefined, false, {}];
};

const visit = (value, parent, key, schema, addEntity, visitedEntities) => {
  if (typeof value !== 'object' || !value || !schema) {
    return value;
  }

  if (typeof schema === 'object' && (!schema.normalize || typeof schema.normalize !== 'function')) {
    const method = Array.isArray(schema) ? normalize : normalize$1;
    return method(schema, value, parent, key, visit, addEntity, visitedEntities);
  }

  return schema.normalize(value, parent, key, visit, addEntity, visitedEntities);
};

const addEntities = (entities, indexes) => (schema, processedEntity, value, parent, key) => {
  const schemaKey = schema.key;
  const id = schema.pk(value, parent, key);

  if (!(schemaKey in entities)) {
    entities[schemaKey] = {};
  }

  const existingEntity = entities[schemaKey][id];

  if (existingEntity) {
    entities[schemaKey][id] = schema.merge(existingEntity, processedEntity);
  } else {
    entities[schemaKey][id] = processedEntity;
  } // update index


  if (Array.isArray(schema.indexes)) {
    const entity = entities[schemaKey][id];

    if (!(schemaKey in indexes)) {
      indexes[schemaKey] = {};
    }

    for (const index of schema.indexes) {
      if (!(index in indexes[schemaKey])) {
        indexes[schemaKey][index] = {};
      }

      const indexMap = indexes[schemaKey][index];

      if (existingEntity) {
        delete indexMap[existingEntity[index]];
      }

      if (index in entity) {
        indexMap[entity[index]] = id;
      }
      /* istanbul ignore next */
      else if ( // eslint-disable-next-line no-undef
        process.env.NODE_ENV !== 'production') {
          console.warn(`Index not found in entity. Indexes must be top-level members of your entity.
Index: ${index}
Entity: ${JSON.stringify(entity, undefined, 2)}`);
        }
    }
  }
};

function expectedSchemaType(schema) {
  return ['object', 'function'].includes(typeof schema) ? 'object' : typeof schema;
}

const normalize$2 = (input, schema) => {
  const schemaType = expectedSchemaType(schema);

  if (input === null || typeof input !== schemaType) {
    /* istanbul ignore else */
    if (process.env.NODE_ENV !== 'production') {
      const parseWorks = input => {
        try {
          return typeof JSON.parse(input) !== 'string';
        } catch (e) {
          return false;
        }
      };

      if (typeof input === 'string' && parseWorks(input)) {
        throw new Error(`Normalizing a string, but this does match schema.

Parsing this input string as JSON worked. This likely indicates fetch function did not parse
the JSON. By default, this only happens if "content-type" header includes "json".
See https://resthooks.io/docs/guides/custom-networking for more information

  Schema: ${JSON.stringify(schema, undefined, 2)}
  Input: "${input}"`);
      } else {
        throw new Error(`Unexpected input given to normalize. Expected type to be "${schemaType}", found "${input === null ? 'null' : typeof input}".

          Schema: ${JSON.stringify(schema, undefined, 2)}
          Input: "${input}"`);
      }
    } else {
      throw new Error(`Unexpected input given to normalize. Expected type to be "${schemaType}", found "${input === null ? 'null' : typeof input}".`);
    }
  }

  const entities = {};
  const indexes = {};
  const addEntity = addEntities(entities, indexes);
  const visitedEntities = {};
  const result = visit(input, input, undefined, schema, addEntity, visitedEntities);
  return {
    entities,
    indexes,
    result
  };
};

class UnionSchema extends PolymorphicSchema {
  constructor(definition, schemaAttribute) {
    if (!schemaAttribute) {
      throw new Error('Expected option "schemaAttribute" not found on UnionSchema.');
    }

    super(definition, schemaAttribute);
  }

  normalize(input, parent, key, visit, addEntity, visitedEntities) {
    return this.normalizeValue(input, parent, key, visit, addEntity, visitedEntities);
  }

  denormalize(input, unvisit) {
    return this.denormalizeValue(input, unvisit);
  }

}

class ValuesSchema extends PolymorphicSchema {
  normalize(input, parent, key, visit, addEntity, visitedEntities) {
    return Object.keys(input).reduce((output, key, index) => {
      const value = input[key];
      return value !== undefined && value !== null ? Object.assign(Object.assign({}, output), {}, {
        [key]: this.normalizeValue(value, input, key, visit, addEntity, visitedEntities)
      }) : output;
    }, {});
  }

  denormalize(input, unvisit) {
    let found = true;
    return [Object.keys(input).reduce((output, key) => {
      const entityOrId = input[key];
      const [value, foundItem] = this.denormalizeValue(entityOrId, unvisit);

      if (!foundItem) {
        found = false;
      }

      return Object.assign(Object.assign({}, output), {}, {
        [key]: value
      });
    }, {}), found];
  }

}

/* istanbul ignore file */

var schema = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Union: UnionSchema,
  Values: ValuesSchema,
  Array: ArraySchema,
  Object: ObjectSchema
});

export { Entity, FlatEntity, SimpleRecord, denormalize$2 as denormalize, isEntity, normalize$2 as normalize, schema };
